import { faUser, faEdit, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { badgeAccessLevelOptions } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { Avatar, AvatarFallback } from 'src/registry/ui/Avatar';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { cn } from 'src/utils';

import type { MembershipGroupMember } from 'src/features/memberships/types';

interface MembershipGroupMemberBlockProps {
    member: MembershipGroupMember;
    canSeeChangeMemberButtons: boolean;
    setSelectedMembershipGroupMember: (member: MembershipGroupMember) => void;
    setSelectedDeleteMembershipGroupMember: (member: MembershipGroupMember) => void;
}
const MembershipGroupMemberBlock = ({
    member,
    canSeeChangeMemberButtons,
    setSelectedMembershipGroupMember,
    setSelectedDeleteMembershipGroupMember,
}: MembershipGroupMemberBlockProps) => {
    const { formatMessage } = useIntl();

    const option = badgeAccessLevelOptions[member.access_level];

    return (
        <GeneralBlock
            className="group relative flex flex-row items-center gap-4 overflow-hidden p-3 py-4"
            key={member.id}
        >
            <Avatar>
                {member.user && <AvatarFallback>{member.user?.name}</AvatarFallback>}{' '}
                {member.invitation && (
                    <AvatarFallback>
                        <FontAwesomeIcon icon={faUser} />
                    </AvatarFallback>
                )}
            </Avatar>
            <div className="xs:items-center flex w-full flex-wrap items-start justify-between gap-2 overflow-hidden sm:flex-row sm:flex-nowrap sm:gap-4">
                <SidePanelBodyAttribute className="xs:w-auto -mt-0.5 w-full overflow-hidden">
                    <SidePanelBodyAttributeBody control={member} className="truncate">
                        {member.user?.name ?? formatMessage(membershipMessages.invitedUser)}
                    </SidePanelBodyAttributeBody>
                    <SidePanelBodyAttributeTitle className="-mt-0.5 truncate">
                        {member.user?.email ?? member.invitation}
                    </SidePanelBodyAttributeTitle>
                </SidePanelBodyAttribute>

                <Badge className={cn(canSeeChangeMemberButtons && 'md:group-hover:hidden')} theme={option.theme}>
                    {formatMessage(option.label)}
                </Badge>
            </div>

            {canSeeChangeMemberButtons && (
                <div className={cn('ml-auto flex shrink-0 gap-2 sm:hidden sm:group-hover:flex')}>
                    <Button onClick={() => setSelectedMembershipGroupMember(member)} size="icon" variant="outline">
                        <FontAwesomeIcon icon={faEdit} />
                    </Button>

                    <Button
                        onClick={() => setSelectedDeleteMembershipGroupMember(member)}
                        size="icon"
                        variant="destructiveLight"
                    >
                        <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                </div>
            )}
        </GeneralBlock>
    );
};

export { MembershipGroupMemberBlock };
